import { objectKeys } from './object.js';
import { isNullish } from '../typeValidation/nullish.js';

const complexTypes = ['object', 'function', 'symbol'];
/**
 * Diff two objects deeply, returning a list of all the differences.
 * Primarily to be used for tracking changes in a tree structure of
 * any nested depth.
 *
 * @param prev - The previous object to compare.
 * @param next - The next object to compare.
 * @param path - The current path of the comparison.
 * @param allowNullish - Whether to allow nullish values in the comparison.
 * @param acc - The accumulator for the differences.
 * @returns The differences between the two objects.
 */

const diffObjectsDeep = (_prev, _next, path = [], allowNullish = false, acc = {}) => {
  const prev = _prev !== null && _prev !== void 0 ? _prev : {};
  const next = _next !== null && _next !== void 0 ? _next : {};
  const result = {};

  const validate = val => {
    if (allowNullish && isNullish(val)) return null;
    return val;
  }; // Get all unique keys from both objects


  const keys = new Set([...objectKeys(prev), ...objectKeys(next)]);
  keys.forEach(key => {
    const newPath = [...path, key];
    const value1 = validate(prev[key]);
    const value2 = validate(next[key]);

    if (typeof value1 === 'object' && value1 !== null && typeof value2 === 'object' && value2 !== null) {
      // Recursive diff for nested objects
      const nestedDiff = diffObjectsDeep(value1, value2, newPath, allowNullish, acc);

      if (Object.keys(nestedDiff).length > 0) {
        // Only add if there are differences
        result[newPath.join('/')] = nestedDiff;
      }
    } else if (value1 !== value2) {
      // Direct comparison for non-objects or nulls
      result[newPath.join('/')] = {
        next: value2,
        prev: value1
      };
      acc[newPath.join('/')] = {
        next: value2,
        prev: value1
      };
    }
  });

  if (path.length === 0) {
    return acc;
  }

  return result;
};
/**
 * Check if two values are strictly equal only if they
 * are both of a literal type (i.e. not objects, functions, or symbols).
 *
 * @param a - The first value to compare.
 * @param b - The second value to compare.
 * @returns Whether the two values are strictly equal.
 */

const literalEqual = (a, b) => {
  if (complexTypes.includes(typeof a) || complexTypes.includes(typeof b)) return false;
  return a === b;
};
const isEqual = (a, b) => Object.keys(diffObjectsDeep(a, b)).length === 0;

export { diffObjectsDeep, isEqual, literalEqual };
